/*
 * NOTE
 * There is a mismatch between what our fields generate as models and what
 * the API expects. This helper formats specific cases to match the
 * payload expected by the API. There are situtations per form
 * and per type. Per type situations can be handled here at
 * getSanitizeFieldPerType() and per form situations at
 * the form passing a function with cases to
 * payloadSanitizerHelper().
 * */

import type { FieldPayload } from '../types';
import { getPayloadDateString, getPayloadDateTimeString } from '@Helpers';

function isInvalidDate(value: any): boolean {
  return value instanceof Date && isNaN(value.getTime());
}

/*
 * NOTE :: The wire is timezone-naive ISO, never the shelter's display format and never
 * `.toISOString()`. `YYYY-MM-DD` for a date, `YYYY-MM-DDTHH:mm:ss` for a date+time —
 * the wall clock the user typed, which the API anchors to the shelter timezone.
 * `.toISOString()` would reframe that clock by the BROWSER timezone and shift the stored
 * instant for any staffer sitting in a different zone from the shelter.
 * */
function getSanitizeFieldPerType({ name, value, type }: FieldPayload) {
  if (!value || isInvalidDate(value)) return;

  // prettier-ignore
  switch (type) {
    case 'date': { return { [name]: getPayloadDateString(value) }; }
    case 'date_iso_string': { return { [name]: getPayloadDateTimeString(value) }; }
    case 'number': { return { [name]: value.toString() }; }
    case 'date_with_time_string': { return { [name]: getPayloadDateTimeString(value) }; }
  }
}

function getSanitizedEntry(
  { name, type, value }: FieldPayload,
  fieldNamesForValidation?: string[],
  getSanitizedFieldPerName?: Function
) {
  if (getSanitizedFieldPerName && fieldNamesForValidation) {
    const NAME_VALIDATION = fieldNamesForValidation;
    const needsNameValidation = NAME_VALIDATION.includes(name);
    if (needsNameValidation) return getSanitizedFieldPerName({ name, value });
  }

  const TYPE_VALIDATION = ['date', 'date_iso_string', 'number', 'date_with_time_string'];
  const needsTypeValidation = type && TYPE_VALIDATION.includes(type);
  if (needsTypeValidation) return getSanitizeFieldPerType({ name, value, type });

  return { [name]: value };
}

export function payloadSanitizerHelper(
  fieldPayload: FieldPayload,
  fieldNamesForValidation: string[],
  getSanitizedFieldPerName: Function
) {
  return getSanitizedEntry(fieldPayload, fieldNamesForValidation, getSanitizedFieldPerName);
}
